import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Student } from '@/types';
import { formatDate, formatNumber, nextDueDate, tuitionStatus } from '@/lib/datetime';
import Field, { inputClass } from '@/components/base/Field';

interface TuitionSectionProps {
  student: Student;
  onUpdated: (updated: Student) => void;
}

const STATUS_META: Record<
  'paid' | 'overdue' | 'upcoming',
  { label: string; badge: string; icon: string }
> = {
  paid: {
    label: '결제완료',
    badge: 'bg-secondary-100 text-secondary-900',
    icon: 'ri-checkbox-circle-line',
  },
  overdue: {
    label: '미납',
    badge: 'bg-accent-100 text-accent-800',
    icon: 'ri-error-warning-line',
  },
  upcoming: {
    label: '결제 예정',
    badge: 'bg-background-200 text-foreground-700',
    icon: 'ri-time-line',
  },
};

function todayKey(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function TuitionSection({ student, onUpdated }: TuitionSectionProps) {
  const [fee, setFee] = useState(String(student.tuition_fee || ''));
  const [dueDay, setDueDay] = useState(String(student.tuition_due_day || 10));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setFee(String(student.tuition_fee || ''));
    setDueDay(String(student.tuition_due_day || 10));
  }, [student.tuition_fee, student.tuition_due_day]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        tuition_fee: fee ? Number(fee) : 0,
        tuition_due_day: dueDay ? Number(dueDay) : 0,
      };
      const { data, error: updateError } = await supabase
        .from('students')
        .update(payload)
        .eq('id', student.id)
        .select();
      if (updateError) throw updateError;
      const updated = (data as Student[] | null)?.[0];
      if (updated) onUpdated(updated);
      setNotice('교습비 정보를 저장했어요.');
      setEditing(false);
    } catch {
      setError('교습비 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const changePaid = async (paid: boolean) => {
    setPaying(true);
    setError('');
    try {
      const { data, error: updateError } = await supabase
        .from('students')
        .update({ tuition_last_paid_at: paid ? todayKey() : null })
        .eq('id', student.id)
        .select();
      if (updateError) throw updateError;
      const updated = (data as Student[] | null)?.[0];
      if (updated) onUpdated(updated);
      setNotice(paid ? '결제완료로 표시했어요.' : '결제완료를 취소했어요.');
    } catch {
      setError(paid ? '결제완료 처리에 실패했어요.' : '결제완료 취소에 실패했어요.');
    } finally {
      setPaying(false);
    }
  };

  const nextDate = nextDueDate(student.tuition_due_day);
  const { status, cycleDue } = tuitionStatus(
    student.tuition_due_day,
    student.tuition_last_paid_at,
  );
  const meta = STATUS_META[status];

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-wallet-3-line"></i>
            </span>
            교습비
            <span
              className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badge}`}
            >
              <i className={meta.icon}></i>
              {meta.label}
            </span>
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            교습비 금액과 매월 결제일을 관리하고, 결제 상태를 표시해요.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className="flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
        >
          <i className={editing ? 'ri-close-line' : 'ri-edit-line'}></i>
          {editing ? '취소' : '수정'}
        </button>
      </div>

      {notice && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-100/70 px-3 py-2 text-xs text-primary-700">
          <i className="ri-information-line"></i>
          {notice}
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {editing ? (
        <form onSubmit={save} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="교습비 (원)">
            <input
              type="number"
              min={0}
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="예: 400000"
              className={inputClass}
            />
          </Field>
          <Field label="결제일 (매월)">
            <select value={dueDay} onChange={(e) => setDueDay(e.target.value)} className={inputClass}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  매월 {n}일
                </option>
              ))}
            </select>
          </Field>
          <div className="flex items-end sm:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              저장
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-primary-50 p-4">
              <p className="text-xs text-foreground-500">교습비</p>
              <p className="mt-1 text-xl font-bold text-primary-700">
                {student.tuition_fee ? `${formatNumber(student.tuition_fee)}원` : '미입력'}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <p className="text-xs text-foreground-500">결제일</p>
              <p className="mt-1 text-xl font-bold text-foreground-900">
                {student.tuition_due_day ? `매월 ${student.tuition_due_day}일` : '미입력'}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-accent-50 p-4">
              <p className="text-xs text-foreground-500">다음 결제일</p>
              <p className="mt-1 text-xl font-bold text-accent-700">{formatDate(nextDate)}</p>
            </div>
          </div>

          <div
            className={`mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${
              status === 'overdue'
                ? 'border-accent-300 bg-accent-50'
                : 'border-background-200 bg-background-100'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <span
                className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg ${meta.badge}`}
              >
                <i className={meta.icon}></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">
                  {status === 'paid' && '이번 회차 결제완료'}
                  {status === 'overdue' && '이번 회차 미납'}
                  {status === 'upcoming' && '이번 회차 결제 예정'}
                </p>
                <p className="mt-0.5 text-xs text-foreground-600">
                  {status === 'paid' &&
                    `${formatDate(student.tuition_last_paid_at)} 납부 완료`}
                  {status === 'overdue' &&
                    `${formatDate(cycleDue)} 결제 예정일이 지났어요. 결제를 받으면 아래 버튼을 눌러주세요.`}
                  {status === 'upcoming' &&
                    `결제 예정일은 ${formatDate(cycleDue)}예요. 미리 받았다면 결제완료로 표시할 수 있어요.`}
                </p>
              </div>
            </div>

            {status === 'paid' ? (
              <button
                type="button"
                onClick={() => changePaid(false)}
                disabled={paying}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200 disabled:opacity-50 whitespace-nowrap"
              >
                {paying ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-arrow-go-back-line"></i>}
                결제완료 취소
              </button>
            ) : (
              <button
                type="button"
                onClick={() => changePaid(true)}
                disabled={paying}
                className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-background-50 transition disabled:opacity-50 whitespace-nowrap ${
                  status === 'overdue'
                    ? 'bg-accent-600 hover:bg-accent-700'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                {paying ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                결제완료
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}